import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useToast } from "@/hooks/use-toast";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { noticeSubjectParams, useViolationNotices } from "@/hooks/use-notices";
import {
  useRecordEnforcementAction,
  useUpdateViolation,
  useViolation,
  useViolationRecord,
  violationKeys,
  type EnforcementActionCreateRequest,
  type EnforcementActionType,
  type ViolationUpdateRequest,
} from "@/hooks/use-violations";
import { caseLetterSubject } from "@/components/violations/case-letter-subject";
import { violationStatusVariant } from "@/components/violations/violations-columns";
import { violationErrorMessage } from "@/components/violations/violation-error";
import { isTerminalStatus, type LifecycleVerb } from "@/components/violations/violation-lifecycle";
import { ViolationLifecycleDialog } from "@/components/violations/violation-lifecycle-dialog";
import { NoteActionDialog } from "@/components/violations/note-action-dialog";
import { ViolationHeaderActions } from "@/components/violations/violation-header-actions";
import { ViolationFactsCard } from "@/components/violations/violation-facts-card";
import { ViolationEvidenceCard } from "@/components/violations/violation-evidence-card";
import { ViolationLettersCard } from "@/components/violations/violation-letters-card";
import { ViolationRecordCard } from "@/components/violations/violation-record-card";
import { ViolationWorkCard } from "@/components/violations/violation-work-card";
import { ViolationAuditCard } from "@/components/violations/violation-audit-card";
import { Badge, Breadcrumbs, Button, PageHeader, PageSkeleton } from "@/components/ui";
import { RelatedTasksPanel } from "@/components/tasks/related-tasks-panel";
import { EntityDocuments } from "@/components/documents/entity-documents";
import { NotesCard } from "@/components/entity-notes/notes-card";
import { FacilityPendingInstallationBanner } from "@/components/change-requests/facility-pending-installation-banner";

export const Route = createFileRoute("/_authenticated/test-activity/violations/$id/")({
  component: ViolationDetailPage,
});

interface PageError {
  title: string;
  message: string;
}

function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}

/**
 * The enforcement case file. The header names the condition, badges the
 * status, the stored degree of hazard and the case's hazard type, and — with
 * `violations:update` on a live case, never otherwise — offers ONE primary
 * verb, the one that closes the case, beside an Actions menu holding the other
 * verbs the status allows and the note of a fee or referral.
 *
 * Beneath, two columns from `xl` up, one stacked column below it. The wide
 * left column holds what the case is: Facts (inline-editable on a live case),
 * the Record (the case's derived record, read once here), the case's internal
 * Notes (a card of their own, open to any member who can read the case) and,
 * once the case carries any, its Resolution evidence. The right column holds what was done
 * about it: Letters (the derived counters above the case's own letters, with
 * Send letter and Compose letter, each behind its notices grants), Scheduled
 * work (the case's assignments, for a caller who may read them or may schedule
 * on this case: Schedule test and Schedule inspection behind
 * `work_assignments:create` on a live case), Documents, then Tasks and the
 * Audit trail, both folded. The note dialog opens from the Actions menu and
 * from the falsified report's referral door; the referral that report requires
 * is read off the same record.
 *
 * A 409 from any write — the case went terminal, or moved, under the page —
 * re-reads the case so the header resnaps to the status the server reports,
 * and renders the typed message at the top of the page. A typed 422 stays in
 * the dialog that caused it, for the operator to correct.
 */
function ViolationDetailPage() {
  const { t } = useTranslation(["violations", "common", "nav"]);
  const { id } = Route.useParams();
  const orgId = useAppStore((s) => s.activeOrgId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toasts = useToast();
  const { can } = usePermissions();

  const { data: violation, isLoading, isError } = useViolation(id);
  useRecentPageTitle(violation ? t(`type.${violation.violation_type}`) : undefined);
  const update = useUpdateViolation(id);
  const note = useRecordEnforcementAction(id);
  const record = useViolationRecord(id);
  const letters = useViolationNotices(id);

  const [dialog, setDialog] = useState<LifecycleVerb | null>(null);
  const [noting, setNoting] = useState<{ initialType?: EnforcementActionType } | null>(null);
  const [pageError, setPageError] = useState<PageError | null>(null);

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !violation) {
    return (
      <div className="p-8">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("errors.loadDetailFailed")}
        </div>
        <div className="mt-4">
          <Link
            to="/test-activity"
            search={{ tab: "violations" }}
            className="text-sm text-primary hover:underline"
          >
            {t("common:back")}
          </Link>
        </div>
      </div>
    );
  }

  const canEdit = can("violations", "update") && !isTerminalStatus(violation.status);
  const canReadLetters = can("notices", "read");
  const canCompose = can("notices", "create");
  // Send letter approves and dispatches in the same request, so it takes the
  // grants the letter page's approve-and-send takes, beside compose's.
  const canSend = canCompose && can("notices", "update") && can("notices", "send");
  const canReadWork = can("work_assignments", "read");
  const canSchedule = can("work_assignments", "create") && !isTerminalStatus(violation.status);
  const hasReferral = (record.data?.items ?? []).some(
    (item) => item.kind === "note" && item.note_type === "referral_to_licensing_board",
  );

  const reread = () => {
    if (orgId) void queryClient.invalidateQueries({ queryKey: violationKeys.detail(orgId, id) });
  };

  const submitLifecycle = async (body: ViolationUpdateRequest) => {
    try {
      await update.mutateAsync(body);
    } catch (err) {
      if (!isConflict(err)) throw err;
      reread();
      setDialog(null);
      setPageError({ title: t("lifecycle.failed"), message: violationErrorMessage(err) });
      return;
    }
    setDialog(null);
    setPageError(null);
    toasts.success("violations:lifecycle.success");
  };

  const patchField = async (body: ViolationUpdateRequest) => {
    try {
      await update.mutateAsync(body);
    } catch (err) {
      if (isConflict(err)) reread();
      setPageError({ title: t("errors.saveFailed"), message: violationErrorMessage(err) });
      return;
    }
    setPageError(null);
    toasts.success("violations:lifecycle.success");
  };

  const submitNote = async (body: EnforcementActionCreateRequest) => {
    try {
      await note.mutateAsync(body);
    } catch (err) {
      if (!isConflict(err)) throw err;
      reread();
      setNoting(null);
      setPageError({ title: t("lifecycle.failed"), message: violationErrorMessage(err) });
      return;
    }
    setNoting(null);
    setPageError(null);
    toasts.success("violations:actions.success");
  };

  const composeLetter = () =>
    void navigate({
      to: "/notices/new",
      search: { violation_id: violation.id, ...noticeSubjectParams(caseLetterSubject(violation)) },
    });

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          {
            label: t("testActivity.violations", { ns: "nav" }),
            to: "/test-activity",
            search: { tab: "violations" },
          },
          { label: t("detail.title") },
        ]}
      />
      <PageHeader
        title={t(`type.${violation.violation_type}`)}
        meta={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={violationStatusVariant(violation.status)}>
              {t(`status.${violation.status}`)}
            </Badge>
            {violation.degree_of_hazard && (
              <Badge
                variant={violation.degree_of_hazard === "health_hazard" ? "danger" : "default"}
              >
                {t(`degree_of_hazard.${violation.degree_of_hazard}`, { ns: "vocabulary" })}
              </Badge>
            )}
            {violation.hazard_type_label && (
              <Badge variant="default">{violation.hazard_type_label}</Badge>
            )}
          </div>
        }
        actions={
          canEdit ? (
            <ViolationHeaderActions
              status={violation.status}
              heldFromStatus={violation.held_from_status}
              onVerb={setDialog}
              onNote={() => setNoting({})}
            />
          ) : undefined
        }
      />

      <FacilityPendingInstallationBanner violation={violation} />

      {pageError && (
        <div
          role="alert"
          className="mt-4 flex items-start justify-between gap-4 rounded-md bg-danger/10 p-4 text-sm"
        >
          <div>
            <p className="font-medium text-danger">{pageError.title}</p>
            <p className="mt-1 text-danger">{pageError.message}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setPageError(null)}>
            {t("common:dismiss")}
          </Button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="min-w-0 space-y-6 xl:col-span-2">
          <ViolationFactsCard violation={violation} canEdit={canEdit} onPatch={patchField} />
          <ViolationRecordCard record={record} canOpenLetters={canReadLetters} />
          <NotesCard parent="violation" parentId={violation.id} />
          <ViolationEvidenceCard violation={violation} />
        </div>

        <div className="min-w-0 space-y-6">
          <ViolationLettersCard
            violation={violation}
            letters={letters}
            canCompose={canCompose}
            canSend={canSend}
            onCompose={composeLetter}
            canOpenLetters={canReadLetters}
          />
          {(canReadWork || canSchedule) && (
            <ViolationWorkCard
              violation={violation}
              canRead={canReadWork}
              canSchedule={canSchedule}
            />
          )}
          {orgId && (
            <EntityDocuments entityType="violation" entityId={violation.id} orgId={orgId} />
          )}
          <RelatedTasksPanel
            entityType="violation"
            entityId={violation.id}
            collapsible
            defaultOpen={false}
          />
          <ViolationAuditCard violationId={violation.id} />
        </div>
      </div>

      {dialog && (
        <ViolationLifecycleDialog
          key={dialog}
          verb={dialog}
          violation={violation}
          hasReferral={hasReferral}
          pending={update.isPending}
          onClose={() => setDialog(null)}
          onSubmit={submitLifecycle}
          onRecordReferral={() => {
            setDialog(null);
            setNoting({ initialType: "referral_to_licensing_board" });
          }}
        />
      )}

      {noting && (
        <NoteActionDialog
          initialType={noting.initialType}
          pending={note.isPending}
          onClose={() => setNoting(null)}
          onSubmit={submitNote}
        />
      )}
    </div>
  );
}
